import { z } from "zod";

/**
 * "Type it twice" — the confirmation option available on any short text or number field.
 *
 * The form this was built for asked for a NIN and a bank account number as *four* fields: the
 * value, then a near-duplicate carrying `mustMatchKey` pointing back at it. That works, but it
 * makes the author build the pair by hand every time, invites a mismatched label or a forgotten
 * `mustMatchKey`, and doubles the columns in every export for a value that is stored once.
 *
 * As a per-field option instead, the second input is generated from the first: same type, same
 * validation, same requiredness, no second field to keep in step. It exists to catch a typo, and
 * a typo in an account number is not cosmetic — it sends a stipend to a stranger.
 *
 * `mustMatchKey` stays supported and unchanged. Published versions that use it keep working
 * exactly as they did, because a published FormVersion is immutable and must render against the
 * questions it actually asked.
 */
export const confirmableConfig = {
  /** Ask for the value a second time and require the two to match before the page advances. */
  requireConfirmation: z.boolean().default(false),
  /** Overrides the generated "Confirm <label>" wording. */
  confirmLabel: z.string().max(200).default(""),
};

/** Defaults, for spreading into a descriptor's `defaultConfig`. The builder derives its controls
 *  from the runtime type of these values, so a boolean here becomes a checkbox in the panel. */
export const confirmableDefaults = { requireConfirmation: false, confirmLabel: "" };

/**
 * The suffix a confirmation answer travels under.
 *
 * It has to reach the server: CLAUDE.md allows exactly one form-validation implementation, used by
 * both sides, so the match has to be checkable in both — which means the second value is submitted
 * rather than compared away in the browser. It is stripped before the answer is stored, so nothing
 * downstream (the profile, the export, the search index) ever sees it.
 *
 * Double underscore because a field `key` is `^[a-z][a-z0-9_]*$` and may end in `_confirm`; it
 * cannot contain two underscores in a row, so this can never collide with a real key.
 */
export const CONFIRM_SUFFIX = "__confirm";

export const confirmKeyFor = (key: string): string => `${key}${CONFIRM_SUFFIX}`;

export const isConfirmKey = (key: string): boolean => key.endsWith(CONFIRM_SUFFIX);

/** Strips every confirmation answer. Applied once, where submitted values become stored answers. */
export const withoutConfirmations = (values: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(values).filter(([key]) => !isConfirmKey(key)));

/** Whether a field's stored config turns the option on. Config arrives as untyped JSONB. */
export const wantsConfirmation = (config: Record<string, unknown>): boolean =>
  config.requireConfirmation === true;

export const confirmLabelFor = (label: string, config: Record<string, unknown>): string => {
  const custom = config.confirmLabel;
  return typeof custom === "string" && custom.trim() ? custom.trim() : `Confirm ${label}`;
};

/**
 * Drops confirmation answers whose field no longer asks for one.
 *
 * The case is a draft outliving an edit: someone starts a form while "Confirm account number" is
 * on, the author republishes with it off, and the resumed draft still carries the second value.
 * The validator rejects unknown keys by design (docs/03-API-SPEC.md §1 step 3), so without this
 * the applicant meets a validation error naming a field that is no longer on the form — with no
 * way to act on it. An orphaned copy of a value is not an error, it is just nothing.
 */
export const dropOrphanConfirmations = (
  values: Record<string, unknown>,
  fields: readonly { key: string; config: Record<string, unknown> }[],
): Record<string, unknown> => {
  const expected = new Set(
    fields
      .filter((field) => wantsConfirmation(field.config))
      .map((field) => confirmKeyFor(field.key)),
  );

  return Object.fromEntries(
    Object.entries(values).filter(([key]) => !isConfirmKey(key) || expected.has(key)),
  );
};
